import type React from "react";
import { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import {
  CheckCircle,
  RadioButtonUnchecked,
  Close,
  Refresh,
} from "@mui/icons-material";
import apiService from "../../../../service/Api";

interface ProgressRow {
  id: number;
  checklist_name: string;
  section_id: number;
  section_name: string;
  list_item_id: number;
  list_item: string;
  description: string | null;
  points: number | null;
  checked: boolean;
  updated_at: string;
}

interface SectionGroup {
  id: number;
  name: string;
  items: ProgressRow[];
}

interface ChecklistDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  streamId: number;
  userId: number;
  checklistId: number;
  checklistName: string;
  percent: number; // weighted percent, passed from the badge that opened this
  completed: number; // raw item count, for the "X of Y items" caption
  total: number;
}

const RingPercent: React.FC<{ percent: number }> = ({ percent }) => {
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  const color = percent === 100 ? "#4BA663" : "#BA7517";

  return (
    <Box sx={{ position: "relative", width: 40, height: 40, flexShrink: 0 }}>
      <svg width="40" height="40" viewBox="0 0 40 40">
        <circle
          cx="20"
          cy="20"
          r={radius}
          fill="none"
          stroke="#f0f0f0"
          strokeWidth="4"
        />
        <circle
          cx="20"
          cy="20"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 20 20)"
        />
      </svg>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 600,
          color,
        }}
      >
        {percent}%
      </Box>
    </Box>
  );
};

const ChecklistDetailDrawer: React.FC<ChecklistDetailDrawerProps> = ({
  open,
  onClose,
  streamId,
  userId,
  checklistId,
  checklistName,
  percent,
  completed,
  total,
}) => {
  const [sections, setSections] = useState<SectionGroup[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProgress = async () => {
    setLoading(true);
    try {
      const res = await apiService.get<{ results: ProgressRow[] }>(
        `/api/checklists/checklist-progress/`,
        {
          stream: streamId,
          user: userId,
          checklist: checklistId,
          page_size: 200,
        },
      );

      const rows = res.data.results ?? [];

      const grouped: SectionGroup[] = [];
      for (const row of rows) {
        let section = grouped.find((s) => s.id === row.section_id);
        if (!section) {
          section = { id: row.section_id, name: row.section_name, items: [] };
          grouped.push(section);
        }
        section.items.push(row);
      }

      setSections(grouped);
    } catch (error) {
      console.error("Failed to load checklist progress:", error);
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, streamId, userId, checklistId]);

  const displayName = sections[0]?.items[0]?.checklist_name ?? checklistName;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 380, p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <RingPercent percent={percent} />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {completed} of {total} items
              </Typography>
            </Box>
          </Box>
          <Box>
            <IconButton size="small" onClick={fetchProgress} disabled={loading}>
              <Refresh fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : sections.length > 0 ? (
          sections.map((section) => (
            <Box key={section.id} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {section.name}
              </Typography>

              <List dense disablePadding>
                {section.items.map((item) => (
                  <ListItem key={item.id} disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {item.checked ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <RadioButtonUnchecked
                          color="disabled"
                          fontSize="small"
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.list_item}
                      secondary={item.description || undefined}
                      primaryTypographyProps={{
                        sx: {
                          textDecoration: item.checked
                            ? "line-through"
                            : "none",
                          color: item.checked
                            ? "text.secondary"
                            : "text.primary",
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ mt: 1 }} />
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No checklist items found.
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default ChecklistDetailDrawer;
